import dayjs from "dayjs";
import { useEffect, useState } from "react";
import {
  DriveTimeDataApiItem,
  FullCalendarEvent,
} from "../organisms/Scheduling/ducks/types";

export function isLoggedIn() {
  return !!localStorage.getItem("@lsToken");
}

export const getInitials = (firstName: string, lastName: string) => {
  const firstInitial = firstName?.charAt(0).toUpperCase() ?? "";
  const lastInitial = lastName?.charAt(0).toUpperCase() ?? "";

  return firstInitial + lastInitial;
};

export const getCompanyInitials = (name: string) => {
  const words = name?.split(" ");
  const initials = words
    ?.slice(0, 2)
    ?.map((word) => word?.charAt(0))
    ?.join("");
  return initials.toUpperCase();
};

export const formatCrewLeaderName = (fullName: string): string => {
  if (!fullName) return "";
  const parts = fullName.split(" ");
  if (parts.length > 1) {
    const firstName = parts?.[0];
    const lastNameInitial = parts?.[1]?.[0]; // Take the first initial of the second part
    if (firstName && lastNameInitial) {
      return `${firstName ?? ""} ${lastNameInitial ?? ""}.`;
    }
    return `${firstName ?? ""}.`;
  }
  return fullName; // Return as is if only one name part
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowDimensions;
}

export const getRandomColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const useStableRandomColors = (count: number) => {
  const [colors, setColors] = useState<string[]>();

  useEffect(() => {
    const newColors = [];
    for (let i = 0; i < count; i++) {
      newColors.push(getRandomColor());
    }
    setColors(newColors);
  }, [count]); // Re-generate if count changes

  return colors;
};

export enum ROLE_TYPE {
  client_manager = "Client manager",
  super_admin = "Super admin",
}

export const getValueBeforeUnderscore = (str: string) => {
  const underscoreIndex = str.indexOf("_");
  if (underscoreIndex !== -1) {
    return `${str.substring(0, underscoreIndex)}_tag`;
  }
  return str; // Return the whole string if no underscore is found
};

export const timezoneList: { value: string; label: string }[] = [
  { value: "Pacific/Midway", label: "(GMT-11:00) Midway Island, Samoa" },
  { value: "Pacific/Honolulu", label: "(GMT-10:00) Hawaii" },
  { value: "America/Juneau", label: "(GMT-8:00) Alaska" },
  { value: "America/Dawson", label: "(GMT-7:00) Dawson, Yukon" },
  { value: "America/Phoenix", label: "(GMT-7:00) Arizona" },
  { value: "America/Tijuana", label: "(GMT-7:00) Tijuana" },
  { value: "America/Los_Angeles", label: "(GMT-7:00) Pacific Time" },
  { value: "America/Boise", label: "(GMT-6:00) Mountain Time" },
  {
    value: "America/Chihuahua",
    label: "(GMT-6:00) Chihuahua, La Paz, Mazatlan",
  },
  { value: "America/Regina", label: "(GMT-6:00) Saskatchewan" },
  { value: "America/Belize", label: "(GMT-6:00) Central America" },
  { value: "America/Chicago", label: "(GMT-5:00) Central Time" },
  {
    value: "America/Mexico_City",
    label: "(GMT-5:00) Guadalajara, Mexico City, Monterrey",
  },
  { value: "America/Bogota", label: "(GMT-5:00) Bogota, Lima, Quito" },
  { value: "America/Lima", label: "(GMT-5:00) Pittsburgh" },
  { value: "America/Detroit", label: "(GMT-4:00) Eastern Time" },
  { value: "America/Caracas", label: "(GMT-4:00) Caracas, La Paz" },
  { value: "America/Santiago", label: "(GMT-3:00) Santiago" },
  { value: "America/Sao_Paulo", label: "(GMT-3:00) Brasilia" },
  { value: "America/Montevideo", label: "(GMT-3:00) Montevideo" },
  {
    value: "America/Argentina/Buenos_Aires",
    label: "(GMT-3:00) Buenos Aires, Georgetown",
  },
  { value: "America/St_Johns", label: "(GMT-2:30) Newfoundland and Labrador" },
  { value: "America/Godthab", label: "(GMT-2:00) Greenland" },
  { value: "Atlantic/Cape_Verde", label: "(GMT-1:00) Cape Verde Islands" },
  { value: "Atlantic/Azores", label: "(GMT+0:00) Azores" },
  { value: "Etc/GMT", label: "(GMT+0:00) UTC" },
  { value: "Africa/Casablanca", label: "(GMT+0:00) Casablanca, Monrovia" },
  { value: "Europe/London", label: "(GMT+1:00) Edinburgh, London" },
  { value: "Europe/Dublin", label: "(GMT+1:00) Dublin" },
  { value: "Europe/Lisbon", label: "(GMT+1:00) Lisbon" },
  { value: "Atlantic/Canary", label: "(GMT+1:00) Canary Islands" },
  { value: "Africa/Algiers", label: "(GMT+1:00) West Central Africa" },
  {
    value: "Europe/Belgrade",
    label: "(GMT+2:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague",
  },
  {
    value: "Europe/Sarajevo",
    label: "(GMT+2:00) Sarajevo, Skopje, Warsaw, Zagreb",
  },
  {
    value: "Europe/Brussels",
    label: "(GMT+2:00) Brussels, Copenhagen, Madrid, Paris",
  },
  {
    value: "Europe/Amsterdam",
    label: "(GMT+2:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
  },
  { value: "Africa/Cairo", label: "(GMT+2:00) Cairo" },
  { value: "Africa/Harare", label: "(GMT+2:00) Harare, Pretoria" },
  { value: "Europe/Berlin", label: "(GMT+2:00) Frankfurt" },
  { value: "Europe/Bucharest", label: "(GMT+3:00) Bucharest" },
  {
    value: "Europe/Helsinki",
    label: "(GMT+3:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius",
  },
  { value: "Europe/Athens", label: "(GMT+3:00) Athens" },
  { value: "Asia/Jerusalem", label: "(GMT+3:00) Jerusalem" },
  {
    value: "Europe/Moscow",
    label: "(GMT+3:00) Istanbul, Minsk, Moscow, St. Petersburg, Volgograd",
  },
  { value: "Asia/Kuwait", label: "(GMT+3:00) Kuwait, Riyadh" },
  { value: "Africa/Nairobi", label: "(GMT+3:00) Nairobi" },
  { value: "Asia/Baghdad", label: "(GMT+3:00) Baghdad" },
  { value: "Asia/Dubai", label: "(GMT+4:00) Abu Dhabi, Muscat" },
  { value: "Asia/Baku", label: "(GMT+4:00) Baku, Tbilisi, Yerevan" },
  { value: "Asia/Tehran", label: "(GMT+4:30) Tehran" },
  { value: "Asia/Kabul", label: "(GMT+4:30) Kabul" },
  { value: "Asia/Yekaterinburg", label: "(GMT+5:00) Ekaterinburg" },
  { value: "Asia/Karachi", label: "(GMT+5:00) Islamabad, Karachi, Tashkent" },
  {
    value: "Asia/Kolkata",
    label: "(GMT+5:30) Chennai, Kolkata, Mumbai, New Delhi",
  },
  { value: "Asia/Colombo", label: "(GMT+5:30) Sri Jayawardenepura" },
  { value: "Asia/Kathmandu", label: "(GMT+5:45) Kathmandu" },
  { value: "Asia/Dhaka", label: "(GMT+6:00) Astana, Dhaka" },
  { value: "Asia/Almaty", label: "(GMT+6:00) Almaty, Novosibirsk" },
  { value: "Asia/Rangoon", label: "(GMT+6:30) Yangon Rangoon" },
  { value: "Asia/Bangkok", label: "(GMT+7:00) Bangkok, Hanoi, Jakarta" },
  { value: "Asia/Krasnoyarsk", label: "(GMT+7:00) Krasnoyarsk" },
  {
    value: "Asia/Shanghai",
    label: "(GMT+8:00) Beijing, Chongqing, Hong Kong SAR, Urumqi",
  },
  { value: "Asia/Kuala_Lumpur", label: "(GMT+8:00) Kuala Lumpur, Singapore" },
  { value: "Asia/Taipei", label: "(GMT+8:00) Taipei" },
  { value: "Australia/Perth", label: "(GMT+8:00) Perth" },
  { value: "Asia/Irkutsk", label: "(GMT+8:00) Irkutsk, Ulaanbaatar" },
  { value: "Asia/Seoul", label: "(GMT+9:00) Seoul" },
  { value: "Asia/Tokyo", label: "(GMT+9:00) Osaka, Sapporo, Tokyo" },
  { value: "Asia/Yakutsk", label: "(GMT+9:00) Yakutsk" },
  { value: "Australia/Darwin", label: "(GMT+9:30) Darwin" },
  { value: "Australia/Adelaide", label: "(GMT+9:30) Adelaide" },
  {
    value: "Australia/Sydney",
    label: "(GMT+10:00) Canberra, Melbourne, Sydney",
  },
  { value: "Australia/Brisbane", label: "(GMT+10:00) Brisbane" },
  { value: "Australia/Hobart", label: "(GMT+10:00) Hobart" },
  { value: "Asia/Vladivostok", label: "(GMT+10:00) Vladivostok" },
  { value: "Pacific/Guam", label: "(GMT+10:00) Guam, Port Moresby" },
  {
    value: "Asia/Magadan",
    label: "(GMT+11:00) Magadan, Solomon Islands, New Caledonia",
  },
  { value: "Asia/Kamchatka", label: "(GMT+12:00) Kamchatka, Marshall Islands" },
  { value: "Pacific/Fiji", label: "(GMT+12:00) Fiji Islands" },
  { value: "Pacific/Auckland", label: "(GMT+12:00) Auckland, Wellington" },
  { value: "Pacific/Tongatapu", label: "(GMT+13:00) Nuku'alofa" },
];

export enum SALE_TYPE {
  base_contract = "Base contract",
  upsale = "Upsell",
  one_off_deal = "One off deal",
}

export enum NET_TERMS {
  due_upon_receipt = "Due upon receipt",
  net_15 = "Net 15",
  net_30 = "Net 30",
  net_45 = "Net 45",
  net_60 = "Net 60",
  net_90 = "Net 90",  
}

export enum BILLING_MODAL {
  upfront_payment = "Upfront payment",
  monthly = "Monthly",
  per_milestone = "Per milestone",
  post_delivery = "Post delivery",
  equal_monthly_payments = "Equal monthly payments",
  per_service_completion = "Per service completion",
}

export enum PRICING_MODAL {
  fixed_price = "Fixed price",
  time_material = "Time & material",
}

export enum SOURCE_TYPE {
  customer_complaint = "Customer complaint",
  internal_request = "Internal request",
}

export enum PROPOSAL_STATUS {
  in_progress = "In progress",
  approval_pending = "Pending approval",
  ready = "Ready",
  sent = "Sent",
  accepted = "Accepted",
  revision = "Revision",
  declined = "Declined",
  expired = "Expired",
  archived = "Archived",
  lost = "Lost",
}

export enum WORK_ORDER_STATUS {
  OPEN = "Open",
  IN_PROGRESS = "In Progress",
  SCHEDULED = "Scheduled",
  CONFIRMATION_PENDING = "Confirmation Pending",
  APPROVAL_PENDING = "Approval Pending",
  COMPLETED = "Completed",
  CANCELED = "Canceled",
  CLOSED = "Closed",
}

export const getProbabilityColor = (probability: string) => {
  const value = parseInt(probability, 10);
  if (value >= 80) return "#D0F2E8";
  if (value >= 40) return "#FFF9D9";
  return "#EFF2F3";
};

export const formatEstimatedHrs = (hrs: number): string => {
  if (!hrs || isNaN(hrs)) return "0 m";

  const totalMinutes = Math.round(hrs * 60); // convert to minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} m`;
  }
  return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
};

export function getWeekPosition(
  weekStart: string,
  weekEnd: string
): "past" | "current" | "future" {
  const startDate = dayjs(weekStart);
  const endDate = dayjs(weekEnd);
  const today = dayjs();

  if (today.isBefore(startDate, "day")) return "future";
  if (today.isAfter(endDate, "day")) return "past";
  return "current";
}

export const getExcludedWeekendDates = (): string[] => {
  const makeDayOnRaw = localStorage.getItem("makeDayOn");
  if (!makeDayOnRaw) return [];
  try {
    const dates: string[] = JSON.parse(makeDayOnRaw);
    return Array.isArray(dates) ? dates : [];
  } catch {
    return [];
  }
};

export const generateWeekendBackgroundEvents = (
  startDate: string,
  endDate: string
) => {
  const events: FullCalendarEvent[] = [];
  const excludedDates = getExcludedWeekendDates();
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isBefore(end) || current.isSame(end, "day")) {
    const dayOfWeek = current.day();
    const formattedDate = current.format("YYYY-MM-DD");
    if (
      (dayOfWeek === 6 || dayOfWeek === 0) &&
      !excludedDates.includes(formattedDate)
    ) {
      events.push({
        id: `temp-bg-weekend-${formattedDate}`,
        start: current.hour(8).minute(0).second(0).toISOString(),
        end: current.hour(19).minute(0).second(0).toISOString(),
        display: "background",
        backgroundColor: "#f5f5f5",
        type: "weekend-bg",
        title: "Day off",
      });
    }
    current = current.add(1, "day");
  }
  return events;
};
// export const generateWeekendBackgroundEvents = (
//   startDate: string,
//   endDate: string
// ) => {
//   const events: FullCalendarEvent[] = [];
//   let current = dayjs(startDate);
//   const end = dayjs(endDate);

//   while (current.isBefore(end) || current.isSame(end, "day")) {
//     const dayOfWeek = current.day();
//     if (dayOfWeek === 6 || dayOfWeek === 0) {
//       // Saturday (6) or Sunday (0)
//       events.push({
//         id: `temp-bg-weekend-${current.format("YYYY-MM-DD")}`,
//         start: current.hour(8).minute(0).second(0).toISOString(),
//         end: current.hour(19).minute(0).second(0).toISOString(),
//         display: "background",
//         backgroundColor: "#f5f5f5",
//         type: "weekend-bg",
//         title: "Non-working day",
//       });
//     }
//     current = current.add(1, "day");
//   }
//   return events;
// };

export const generateEventsWithDriveTimes = async (
  initialVisitEvents: FullCalendarEvent[],
  driveTimeData: DriveTimeDataApiItem[]
): Promise<FullCalendarEvent[]> => {
  const processedEvents: FullCalendarEvent[] = [];

  const sortedVisitEvents = [...initialVisitEvents].sort((a, b) =>
    dayjs(a.start).diff(dayjs(b.start))
  );

  let currentEffectiveEndTime: dayjs.Dayjs | null = null;

  for (let i = 0; i < sortedVisitEvents.length; i++) {
    const currentVisit = { ...sortedVisitEvents[i] };
    if (
      currentEffectiveEndTime &&
      dayjs(currentVisit.start).isBefore(currentEffectiveEndTime)
    ) {
      const originalDuration = dayjs(currentVisit.end).diff(
        dayjs(currentVisit.start)
      );
      currentVisit.start = currentEffectiveEndTime.toISOString();
      currentVisit.end = dayjs(currentEffectiveEndTime)
        .add(originalDuration, "millisecond")
        .toISOString();
    }

    processedEvents.push(currentVisit);
    currentEffectiveEndTime = dayjs(currentVisit.end);

    if (i < sortedVisitEvents.length - 1) {
      const nextVisit = sortedVisitEvents[i + 1];

      const isSameCalendarDay = dayjs(currentVisit.end).isSame(
        dayjs(nextVisit.start),
        "day"
      );

      if (currentVisit.origin && nextVisit.origin && isSameCalendarDay) {
        const fromCoordString = `${currentVisit.origin.lat},${currentVisit.origin.lng}`;
        const toCoordString = `${nextVisit.origin.lat},${nextVisit.origin.lng}`;

        const foundDriveTime = driveTimeData.find(
          (data) => data.from === fromCoordString && data.to === toCoordString
        );

        if (foundDriveTime && foundDriveTime.duration_seconds > 0) {
          const driveStart = currentEffectiveEndTime;
          const duration = foundDriveTime.duration_seconds;
          const driveEnd = driveStart.add(duration, "second");
          processedEvents.push({
            id: `drive-${currentVisit.id}-to-${nextVisit.id}`,
            title: `Drive to ${foundDriveTime?.duration_text}`,
            start: driveStart.toISOString(),
            end: driveEnd.toISOString(),
            type: "drive",
            driveTimeMinutes: foundDriveTime?.duration_text || "0",
            driveTimeSeconds: foundDriveTime?.duration_seconds || 0,
          });
          currentEffectiveEndTime = driveEnd;
        }
      }
    }
  }
  return processedEvents;
};

// const generateEventsWithDriveTimes = async (
//   initialVisitEvents: FullCalendarEvent[],
//   driveTimeData: DriveTimeDataApiItem[]
// ): Promise<FullCalendarEvent[]> => {
//   const processedEvents: FullCalendarEvent[] = [];

//   const sortedVisitEvents = [...initialVisitEvents].sort((a, b) =>
//     dayjs(a.start).diff(dayjs(b.start))
//   );

//   let currentEffectiveEndTime: dayjs.Dayjs | null = null;

//   for (let i = 0; i < sortedVisitEvents.length; i++) {
//     const currentVisit = { ...sortedVisitEvents[i] };
//     if (
//       currentEffectiveEndTime &&
//       dayjs(currentVisit.start).isBefore(currentEffectiveEndTime)
//     ) {
//       const originalDuration = dayjs(currentVisit.end).diff(
//         dayjs(currentVisit.start)
//       );
//       currentVisit.start = currentEffectiveEndTime.toISOString();
//       currentVisit.end = dayjs(currentEffectiveEndTime)
//         .add(originalDuration, "millisecond")
//         .toISOString();
//     }

//     processedEvents.push(currentVisit);
//     currentEffectiveEndTime = dayjs(currentVisit.end);

//     if (i < sortedVisitEvents.length - 1) {
//       const nextVisit = sortedVisitEvents[i + 1];

//       const isSameCalendarDay = dayjs(currentVisit.end).isSame(
//         dayjs(nextVisit.start),
//         "day"
//       );

//       if (currentVisit.origin && nextVisit.origin && isSameCalendarDay) {
//         const fromCoordString = `${currentVisit.origin.lat},${currentVisit.origin.lng}`;
//         const toCoordString = `${nextVisit.origin.lat},${nextVisit.origin.lng}`;

//         const foundDriveTime = driveTimeData.find(
//           (data) => data.from === fromCoordString && data.to === toCoordString
//         );

//         if (foundDriveTime && foundDriveTime.duration_seconds > 0) {
//           const driveStart = currentEffectiveEndTime;
//           const duration = foundDriveTime.duration_seconds + 400;
//           // if (foundDriveTime.duration_seconds < 500) {
//           //   duration = foundDriveTime.duration_seconds + 500;
//           // } else {
//           //   duration = foundDriveTime.duration_seconds;
//           // }
//           const driveEnd = driveStart.add(duration, "second");
//           processedEvents.push({
//             id: `drive-${currentVisit.id}-to-${nextVisit.id}`,
//             title: `Drive to ${"next location"}`,
//             start: driveStart.toISOString(),
//             end: driveEnd.toISOString(),
//             editable: false,
//             // extendedProps: {
//             //   type: "drive",
//             //   driveTimeMinutes: Math.ceil(duration / 60),
//             //   location: currentVisit.extendedProps?.location,
//             //   nextVisitLocation: nextVisit.extendedProps?.location,
//             //   backgroundColor: "#E3F2FD",
//             //   borderColor: "#2196F3",
//             //   textColor: "#0D47A1",
//             // },
//           });
//           currentEffectiveEndTime = driveEnd;
//         }
//       }
//     }
//   }
//   return processedEvents;
// };

export const calculateHours = (actualHours: number) => {
  const hours = Math.floor(Number(actualHours));
  const minutes = Math.round((Number(actualHours) % 1) * 60);
  if (minutes === 0) {
    return `${hours}`;
  }
  return `${hours}.${minutes < 10 ? "0" : ""}${minutes}`;
};

export const POSITION_NAMES: Record<string, string> = {
  1: "Manager",
  2: "Crew leader",
  3: "Technician",
  4: "Account manager",
  5: "Production manager",
  6: "CEO",
  super_admin: "Super admin",
};

export const PAGE_LIMIT = 20;